/**
 * Parses the v2 audit-tags blob saved by the bus-detail Audit Tags configuration tab
 * into a flat seat → (channel, position) lookup for the audit detail screen.
 *
 * v2 blob shape (one per bus, stored as the TEXT column Bus.auditSeatTagsConfig):
 * {
 *   "version": 2,
 *   "cameraAspectRatio": "16:9",
 *   "detectedFromLive": "ISO date | null",
 *   "channels": {
 *     "1": "<Fabric.js canvas JSON string for channel 1>",
 *     "2": "...",
 *     "3": "...",
 *     "4": "..."
 *   }
 * }
 *
 * Each channel canvas was rendered at exactly the LIVE camera's pixel ratio (no
 * dark bands), so its own `width` / `height` define the coordinate space for
 * `relX` / `relY` — which are field-of-view fractions (0..1). The audit detail
 * screen renders each photo at its OWN intrinsic shape (the recording, e.g.
 * 16:9) and positions the overlay at those fractions, so the tag lands on the
 * same seat even though the live (config) and recorded (display) aspect ratios
 * differ — Wialon's live stream is just the same field of view down-sampled.
 *
 * Legacy v1 blobs (single unified canvas) are detected and reported via
 * `isLegacy: true` so the caller can show a "needs reconfiguration" UI.
 */

// Default confirmed empirically: every extracted JPEG on staging is 1280×720 (16:9).
export const DEFAULT_CAMERA_ASPECT_RATIO = '16:9';

export type Channel = 1 | 2 | 3 | 4;

export interface SeatTagPosition {
  seatNumber: string; // label text extracted from the Fabric text child
  channel: Channel;
  relX: number; // 0..1, center of tag within its channel's canvas
  relY: number;
  relW: number; // 0..1, tag's width relative to channel canvas
  relH: number;
  fillColor: string;
}

export interface AuditSeatTagLayout {
  cameraAspectRatio: string; // "16:9" | "4:3" | "5:4" | "3:2" | "custom:<decimal>"
  seats: Map<string, SeatTagPosition>;
  isLegacy: boolean; // v1 blob detected
}

interface FabricObjectLike {
  type?: string;
  left?: number;
  top?: number;
  width?: number;
  height?: number;
  scaleX?: number;
  scaleY?: number;
  fill?: string;
  text?: string;
  objects?: FabricObjectLike[];
}

interface FabricCanvasLike {
  width?: number;
  height?: number;
  viewportTransform?: number[];
  objects?: FabricObjectLike[];
}

interface ConfigBlobV2 {
  version: 2;
  cameraAspectRatio?: string;
  detectedFromLive?: string | null;
  channels: Record<string, string>;
}

const emptyLayout = (): AuditSeatTagLayout => ({
  cameraAspectRatio: DEFAULT_CAMERA_ASPECT_RATIO,
  seats: new Map(),
  isLegacy: false,
});

// The Audit Tags config tab renders every channel canvas at exactly this height
// (`cellHeight`) and a width of round(height × ratio). Fabric's `toJSON()` does
// NOT serialize canvas width/height, so configs saved that way carry neither.
// We reconstruct that exact coordinate space from the stored cameraAspectRatio —
// relX/relY are ratios, so the reconstructed dims position tags identically.
const CONFIG_CANVAS_HEIGHT = 380;

/** "16:9" / "3:2" / "custom:1.778" → numeric width/height ratio (fallback 16:9). */
function ratioToValue(ratio: string | undefined): number {
  if (!ratio) return 16 / 9;
  if (ratio.startsWith('custom:')) {
    const n = Number.parseFloat(ratio.slice('custom:'.length));
    return Number.isFinite(n) && n > 0 ? n : 16 / 9;
  }
  const [w, h] = ratio.split(':').map(Number);
  return w > 0 && h > 0 ? w / h : 16 / 9;
}

export function parseAuditSeatTagLayout(raw: string | null | undefined): AuditSeatTagLayout {
  if (!raw) return emptyLayout();

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return emptyLayout();
  }
  if (!parsed || typeof parsed !== 'object') return emptyLayout();

  const blob = parsed as Partial<ConfigBlobV2>;
  if (blob.version !== 2 || !blob.channels) {
    // Anything that isn't explicitly v2 is treated as legacy.
    return { ...emptyLayout(), isLegacy: true };
  }

  const seats = new Map<string, SeatTagPosition>();

  // Coordinate space to fall back to when the saved canvas JSON has no dims
  // (Fabric.toJSON() omits them). Reconstructed from the stored aspect ratio.
  const fallbackH = CONFIG_CANVAS_HEIGHT;
  const fallbackW = Math.round(CONFIG_CANVAS_HEIGHT * ratioToValue(blob.cameraAspectRatio));

  for (const [chStr, canvasJson] of Object.entries(blob.channels)) {
    const channel = Number(chStr) as Channel;
    if (channel < 1 || channel > 4) continue;
    if (!canvasJson) continue;

    let canvas: FabricCanvasLike;
    try {
      canvas = JSON.parse(canvasJson) as FabricCanvasLike;
    } catch {
      continue;
    }

    const cw = canvas.width ?? canvas.viewportTransform?.[0] ?? fallbackW;
    const ch = canvas.height ?? canvas.viewportTransform?.[3] ?? fallbackH;
    if (!cw || !ch) continue;

    for (const obj of canvas.objects ?? []) {
      if ((obj.type ?? '').toLowerCase() !== 'group') continue;
      const children = obj.objects ?? [];
      const text = children.find((c) => /text/i.test(c.type ?? ''));
      const ellipse = children.find((c) => /ellipse/i.test(c.type ?? ''));
      const label = text?.text?.toString().trim();
      if (!label) continue;

      const w = (obj.width ?? 60) * (obj.scaleX ?? 1);
      const h = (obj.height ?? 60) * (obj.scaleY ?? 1);
      const cx = (obj.left ?? 0) + w / 2;
      const cy = (obj.top ?? 0) + h / 2;

      seats.set(label, {
        seatNumber: label,
        channel,
        relX: cx / cw,
        relY: cy / ch,
        relW: w / cw,
        relH: h / ch,
        fillColor: ellipse?.fill ?? '#9ca3af',
      });
    }
  }

  return {
    cameraAspectRatio: blob.cameraAspectRatio ?? DEFAULT_CAMERA_ASPECT_RATIO,
    seats,
    isLegacy: false,
  };
}
